/**
 * Phase 4b PR 5 — Shared Context pills dedupe.
 *
 * Tech-debt #0b fix: vanilla's `context-ui.js` shows duplicate pills
 * when the chat-extraction pipeline writes multiple values for the
 * same key (e.g., `Sport: cross-country` AND `Sport: running`).
 *
 * Dedupe rules (in priority order):
 *   1. Exact key match → most recently written value wins. Earlier
 *      pills with the same key are dropped.
 *   2. Hidden keys → entries whose key starts with `_` (underscore-
 *      prefixed = system metadata) are excluded entirely.
 *   3. Empty / whitespace-only values → excluded.
 *   4. Order preserved → earliest-position dictates display order
 *      (the LATEST value gets pinned to the EARLIEST position so the
 *      pill UI doesn't reflow when context updates).
 *   5. B475 → the weekly distance is one pill: the band id is never a
 *      pill, and the exact entry and the vanilla aliases fold into the
 *      `Weekly mileage` pill. EXCEPTION to rule 1: that pill's value is the
 *      figure the server's read order picks (`weeklyFigure`), not the last
 *      key written (jsonb keeps no write order).
 *
 * Pure function — input is a Record-shaped object that mirrors the
 * vanilla `state.athleteContext` JSONB.
 *
 * B349 — a weekly mileage figure is stated in the athlete's display unit. The
 * stored figure is miles unless its own text names a unit (see
 * `weeklyMileageInUnit`), so a km athlete's "36" reads "58 km", never a bare 36.
 */
import { fromMiles, toMiles, type DistanceUnit } from './actualForPlanDay';
import { resolveDistanceUnit } from './vdot';

export interface ContextPill {
  /** The canonical key (e.g., "Sport", "Weekly mileage"). */
  key: string;
  /** The de-duplicated value to display. */
  value: string;
}

export interface DedupeOptions {
  /** Override the underscore-prefix exclusion. Defaults to true. */
  excludeUnderscorePrefixed?: boolean;
}

/**
 * Keys we never surface to the athlete UI. These are nested context
 * blocks that get flattened separately (see `useAthleteContext`) — the
 * raw `coach_visible` / `coach_private` keys themselves should NEVER
 * render as pills.
 *
 * Round-2 smoke-test 2026-04-29 caught: `coach_private` was leaking
 * into the Shared Context modal as a pill. Hard-exclude.
 */
const NEVER_AS_PILL = new Set(['coach_private', 'coach_visible']);

/**
 * Build a pill list from athlete context data. Vanilla uses a similar
 * shape — see src/renderer/js/context-ui.js. The dedupe rules here
 * are a strict superset of vanilla's behavior plus the #0b fix.
 *
 * Input shape: a flat key → value map. Nested context_visible /
 * context_private blocks should be flattened by the caller before
 * passing in (the `useAthleteContext` hook handles that).
 */
export function dedupePills(
  context: Record<string, unknown> | null | undefined,
  options: DedupeOptions = {},
): ContextPill[] {
  if (!context || typeof context !== 'object') return [];
  const excludeUnderscore = options.excludeUnderscorePrefixed ?? true;

  // Use a Map to preserve insertion order while collapsing duplicate
  // keys. JavaScript Object iteration order is insertion-stable, but
  // we rebuild via Map for clarity.
  const collected = new Map<string, string>();
  const unit = resolveDistanceUnit(context);

  for (const [rawKey, rawValue] of Object.entries(context)) {
    if (excludeUnderscore && rawKey.startsWith('_')) continue;
    // Hard-exclude the nested-block names themselves — they get
    // flattened by the caller; rendering them as pills would surface
    // private coach metadata to the athlete (Round-2 smoke-test fix).
    if (NEVER_AS_PILL.has(rawKey.toLowerCase())) continue;

    const key = canonicalizeKey(rawKey);
    if (!key) continue;

    const value = formatValue(rawValue);
    if (!value) continue; // empty / whitespace-only

    // B475 — the band id ("10_20") is metadata the plan engine reads, never an
    // athlete-facing fact; as a pill it printed the raw id under its raw key.
    if (key === WEEKLY_BAND_KEY) continue;

    // Canonical key collision — last write wins, but position stays
    // at the earliest insertion (Map.set on an existing key updates
    // the value but preserves the original position).
    collected.set(
      key,
      MILEAGE_PILL_KEYS.has(key) ? (weeklyMileageInUnit(value, unit) ?? value) : value,
    );
  }

  // B475 fix round 2 — the weekly figure is ONE pill whose value comes from the
  // server's read order (`weeklyFigure`), never from the order the keys arrived
  // in: Postgres jsonb has no write order (it hands keys back by length, then
  // bytewise, so `Weekly mileage` precedes `weekly_mileage`), and "last key wins"
  // showed the onboarding 30 beside the Settings 40 the engine reads. The pill
  // sits where its earliest key sat. The exact entry and the vanilla aliases
  // restate the figure, so they fold into it.
  const figure = weeklyFigure(context);
  if (figure) {
    const out: ContextPill[] = [];
    let placed = false;
    for (const [key, value] of collected) {
      if (key === 'Weekly mileage' || WEEKLY_ALIAS_LABELS.includes(key)) {
        if (!placed) out.push({ key: 'Weekly mileage', value: figure.value });
        placed = true;
        continue;
      }
      if (key === 'Weekly mileage exact') continue;
      out.push({ key, value });
    }
    return out;
  }

  // B475 — no usable figure by the read order (a placeholder, an extractor
  // spelling the server does not read). Where a `Weekly mileage` pill exists the
  // others restate it and go; the exact entry still shows when it is the only
  // statement of the figure there is.
  if (collected.has('Weekly mileage')) {
    for (const repeat of WEEKLY_MILEAGE_REPEATS) collected.delete(repeat);
  }

  return Array.from(collected.entries()).map(([key, value]) => ({ key, value }));
}

/** The canonical pill keys whose figure is a weekly distance (miles unless it says otherwise). */
const MILEAGE_PILL_KEYS = new Set(['Weekly mileage', 'Weekly mileage exact']);

/** B475 — the stored band id for the weekly figure (`weekly_mileage_band`, e.g. "10_20"): never a pill. */
const WEEKLY_BAND_KEY = 'Weekly mileage band';

/**
 * B475 — the keys that restate the figure `Weekly mileage` already carries: the
 * exact entry (`weekly_mileage_exact`) and the vanilla aliases Settings and the
 * server's one read order (`api/lib/weeklyMileageRead.js`) fall back to.
 */
const WEEKLY_MILEAGE_REPEATS = [
  'Weekly mileage exact',
  'Current weekly distance',
  'Current weekly volume',
  'Weekly distance',
  'Current weekly km',
];

/**
 * B475 fix round 1 — the one rule the three readers of the weekly figure share
 * (these pills, "What Airo knows", and removing the pill): is this STORED key
 * the band id, a restatement of the figure (the exact entry, a vanilla alias),
 * or the figure itself? Matched on the canonical form, so every spelling counts
 * (`weekly_mileage_exact`, `Weekly mileage exact`, `weekly mileage exact`).
 */
export function isWeeklyMileageBandKey(rawKey: string): boolean {
  return canonicalizeKey(rawKey) === WEEKLY_BAND_KEY;
}
export function isWeeklyMileageRepeatKey(rawKey: string): boolean {
  return WEEKLY_MILEAGE_REPEATS.includes(canonicalizeKey(rawKey));
}
export function isWeeklyMileageFigureKey(rawKey: string): boolean {
  return canonicalizeKey(rawKey) === 'Weekly mileage';
}
/** The vanilla aliases of the figure (not the exact entry): a stored key that can BE the figure in the read order. */
export function isWeeklyMileageAliasKey(rawKey: string): boolean {
  return WEEKLY_ALIAS_LABELS.includes(canonicalizeKey(rawKey));
}

/**
 * B475 fix round 2 — the server's read order for the weekly figure, by EXACT stored
 * key: `api/lib/weeklyMileageRead.js` `WEEKLY_MILEAGE_PROMPT_KEYS` (the same order
 * minus the internal `_weekly_mileage`, which no writer sets). It is the COACH
 * PROMPT's read order, the longest of the lists the server builds from the one order
 * (B372, ruling 17): the plan engine reads only the first two keys
 * (`WEEKLY_MILEAGE_PLAN_KEYS`) and the clinical readers the first three
 * (`WEEKLY_MILEAGE_ENGINE_KEYS`). So the figure is the one the coaches read, and the
 * engine plans from the same figure only while it sits under `Weekly mileage` or
 * `weekly_mileage`; a figure stored only under an older alias is one the prompt reads
 * and the engine does not. The prompt read also folds `weekly_miles` (always) and
 * `weekly_total` (with `context_janitor_v1` on) into `weekly_mileage`
 * (`CONTEXT_ALIAS_MAP`, `normalizeContextForRead`); this list does not, so those two
 * stay pills of their own. A test imports the server's prompt list and fails if the two
 * ever differ.
 */
export const WEEKLY_FIGURE_READ_ORDER: readonly string[] = [
  'Weekly mileage',
  'weekly_mileage',
  'Current weekly distance',
  'Current weekly volume',
  'Weekly distance',
  'Current weekly km',
];
const WEEKLY_ALIAS_LABELS: readonly string[] = WEEKLY_FIGURE_READ_ORDER.slice(2);
/** The server's placeholders (`api/lib/weeklyMileageRead.js`): a value that is not a figure. */
const WEEKLY_PLACEHOLDERS = ['not specified', 'unknown', 'n/a'];

function isUsableWeeklyValue(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v !== 'string') return false;
  const t = v.trim();
  return t !== '' && !WEEKLY_PLACEHOLDERS.includes(t.toLowerCase());
}

/**
 * The key and stored value the read order picks: the first key of `WEEKLY_FIGURE_READ_ORDER`
 * the context holds with a usable value. Same rule, same answer as the server's
 * `pickWeeklyMileage(ctx, WEEKLY_MILEAGE_PROMPT_KEYS)`, whatever order the keys sit in.
 */
export function pickWeeklyFigure(
  context: Record<string, unknown> | null | undefined,
): { key: string; value: unknown } | null {
  if (!context || typeof context !== 'object') return null;
  for (const key of WEEKLY_FIGURE_READ_ORDER) {
    if (Object.prototype.hasOwnProperty.call(context, key) && isUsableWeeklyValue(context[key])) {
      return { key, value: context[key] };
    }
  }
  return null;
}

/**
 * B475 fix round 2 — THE ONE WEEKLY FIGURE, as every surface states it: picked by the
 * server's read order, then stated in the athlete's unit through `weeklyMileageInUnit`
 * (a bare figure is miles, except under `Current weekly km`, where it is km, as the
 * server reads it). The context pills and "What Airo knows" both show exactly
 * `value`, so the rail's "19 km" is the section's "19 km". `key` is the stored key the
 * figure came from.
 */
export function weeklyFigure(
  context: Record<string, unknown> | null | undefined,
): { key: string; value: string } | null {
  const picked = pickWeeklyFigure(context);
  if (!picked || !context) return null;
  const text = formatValue(picked.value);
  const stated = picked.key === 'Current weekly km' && /^\d+(?:\.\d+)?$/.test(text) ? `${text} km` : text;
  return { key: picked.key, value: weeklyMileageInUnit(stated, resolveDistanceUnit(context)) ?? text };
}

/**
 * The value a weekly-distance key reads as in the athlete's unit (the figure and the
 * exact entry, which are miles unless their own text names a unit); every other key's
 * value comes back as it is. For a reader that lists a weekly key on its own.
 */
export function weeklyValueInUnit(
  rawKey: string,
  value: string,
  context: Record<string, unknown>,
): string {
  return MILEAGE_PILL_KEYS.has(canonicalizeKey(rawKey))
    ? (weeklyMileageInUnit(value, resolveDistanceUnit(context)) ?? value)
    : value;
}

// "36", "36 miles", "30 mi/week", "40 km a week", "30 to 40 miles": a figure (or a
// range), an optional unit, an optional "per week" tail. Nothing else.
const WEEKLY_MILEAGE_RE =
  /^\s*(\d+(?:\.\d+)?)(?:\s*(?:-|–|to)\s*(\d+(?:\.\d+)?))?\s*(miles?|mi|kilomet(?:er|re)s?|kms?)?\.?(\s*(?:\/|\ba\b|\bper\b)\s*(?:week|wk)\.?)?\s*$/i;

/**
 * B349 — a stored weekly mileage figure, stated in the athlete's display unit.
 *
 * What is stored: onboarding writes `weekly_mileage` in MILES (the engine's unit,
 * `lib/firstPlan/trainingData.ts`; an exact entry in km is converted on the way in),
 * and chat extraction stores the bare number the engine reads as miles
 * (`weekMaterializer.js`: "weekly mileage (miles, by contract)"). Settings keeps free
 * text ("30 mi/week"), so a figure that names its own unit is read in that unit.
 *
 * So: a text already in the athlete's unit comes back exactly as written (a mile
 * athlete's "36" stays "36"); a figure in the other unit is converted to whole
 * numbers with the unit named ("36" for a km athlete reads "58 km"); a range keeps
 * both ends. A text that is not a plain figure (prose) comes back as written for a
 * mile athlete and as `null` for a km athlete, because its unit cannot be stated.
 */
export function weeklyMileageInUnit(raw: string, unit: DistanceUnit): string | null {
  const m = WEEKLY_MILEAGE_RE.exec(raw);
  if (!m) return unit === 'mi' || !/\d/.test(raw) ? raw : null;
  const [, first, second, unitRaw, tail = ''] = m;
  const stated: DistanceUnit = unitRaw && /^k/i.test(unitRaw) ? 'km' : 'mi';
  if (stated === unit) return raw;
  const convert = (n: string): number =>
    Math.round(stated === 'mi' ? fromMiles(Number(n), unit) : toMiles(Number(n), 'km'));
  const figure = second ? `${convert(first)} to ${convert(second)}` : String(convert(first));
  return `${figure} ${unit}${tail}`;
}

function canonicalizeKey(raw: string): string {
  // Normalize:
  //   - trim whitespace
  //   - replace underscores with spaces (so `Weekly_mileage` collapses
  //     with `Weekly mileage` — Round-2 smoke-test fix from 2026-04-29
  //     where backend extractors emit snake_case while UI emits the
  //     spaced form)
  //   - collapse internal whitespace runs to a single space
  //   - title-case the first letter to match vanilla display
  const trimmed = String(raw || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
  if (!trimmed) return '';
  // Lowercase first to normalize case before title-casing the first
  // letter — so `weekly mileage` and `Weekly_mileage` both collapse
  // to `Weekly mileage`.
  const lower = trimmed.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function formatValue(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'boolean') return raw ? 'yes' : 'no';
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : '';
  if (typeof raw === 'string') return raw.trim();
  // Arrays — comma-join (e.g., goals: ['5K','marathon'] → "5K, marathon")
  if (Array.isArray(raw)) {
    return raw
      .map((x) => formatValue(x))
      .filter(Boolean)
      .join(', ');
  }
  // Objects — flatten one level (best-effort for nested context blocks)
  if (typeof raw === 'object') {
    try {
      return Object.values(raw as Record<string, unknown>)
        .map((v) => formatValue(v))
        .filter(Boolean)
        .join(', ');
    } catch {
      return '';
    }
  }
  return String(raw).trim();
}
